'use client';

import Image, { ImageProps } from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

const RETRY_COUNT = 3;
const RETRY_DELAY_MS = 1000;

export default function ImageWithRetry({ ...props }: ImageProps) {
  const [src, setSrc] = useState(props.src);
  const [originalSrc, setOriginalSrc] = useState(props.src);
  const [isFailed, setIsFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearRetryTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const generateRetryUrl = useCallback((originalSrc: ImageProps['src'], retryAttempt: number) => {
    // StaticImageData 객체인지 확인하고 적절한 src 값을 추출
    let srcStr: string;
    if (typeof originalSrc === 'object' && originalSrc !== null && 'src' in originalSrc) {
      srcStr = originalSrc.src;
    } else {
      srcStr = String(originalSrc);
    }

    if (srcStr.startsWith('http://') || srcStr.startsWith('https://')) {
      try {
        const url = new URL(srcStr);
        url.searchParams.set('retryCount', retryAttempt.toString());
        url.searchParams.set('t', Date.now().toString());
        return url.toString();
      } catch {
        const separator = srcStr.includes('?') ? '&' : '?';
        return `${srcStr}${separator}retryCount=${retryAttempt}&t=${Date.now()}`;
      }
    } else if (srcStr.startsWith('data:') || srcStr.startsWith('blob:')) {
      // data: 또는 blob: URL의 경우 쿼리 파라미터를 추가하지 않음
      return srcStr;
    } else {
      const separator = srcStr.includes('?') ? '&' : '?';
      return `${srcStr}${separator}retryCount=${retryAttempt}&t=${Date.now()}`;
    }
  }, []);

  const handleError = useCallback(() => {
    if (isRetrying || isFailed) {
      return;
    }

    if (retryCount < RETRY_COUNT) {
      setIsRetrying(true);
      clearRetryTimeout();

      timeoutRef.current = setTimeout(() => {
        // 타임아웃 실행 시점에 현재 originalSrc를 사용
        const currentSrc = originalSrc;
        const newRetryCount = retryCount + 1;

        setRetryCount(newRetryCount);
        setSrc(generateRetryUrl(currentSrc, newRetryCount));
        setIsRetrying(false);
      }, RETRY_DELAY_MS);
    } else {
      setIsFailed(true);
    }
  }, [retryCount, isRetrying, isFailed, originalSrc, clearRetryTimeout, generateRetryUrl]);

  useEffect(() => {
    return () => {
      clearRetryTimeout();
    };
  }, [clearRetryTimeout]);

  useEffect(() => {
    // props.src가 변경되면 즉시 재시도 상태를 리셋하고 타임아웃을 클리어
    if (props.src !== originalSrc) {
      clearRetryTimeout();
      setSrc(props.src);
      setOriginalSrc(props.src);
      setRetryCount(0);
      setIsFailed(false);
      setIsRetrying(false);
    }
  }, [props.src, originalSrc, clearRetryTimeout]);

  if (isFailed) {
    return null;
  }

  if (isRetrying) {
    return <div className={`${props.className} skeleton-item`}></div>;
  }

  return <Image {...props} src={src} onError={handleError} />;
}
